/** Dependency-free capture shared by the inline reload handler and hydrated code. */
export const recoveryDiagnosticsScript = `
(function () {
    var HISTORY_KEY = 'kalshi-recovery-history';
    function cleanUrl(value) {
        if (typeof value !== 'string' || !value) return undefined;
        try {
            var url = new URL(value, window.location.href);
            if (url.protocol !== 'https:' && url.protocol !== 'http:') return undefined;
            return (url.origin + url.pathname).slice(0, 1000);
        } catch (e) { return undefined; }
    }
    function cleanText(value, limit) {
        if (typeof value !== 'string') return undefined;
        return value.replace(/https?:\\/\\/[^\\s"'<>]+|\\/[^\\s"'<>]*[?#][^\\s"'<>]*/g, function (url) {
            return cleanUrl(url) || '[url]';
        }).slice(0, limit);
    }
    function capture(trigger, error, target, filename) {
        var now = Date.now();
        var result = {
            recovery_id: now.toString(36) + '-' + Math.random().toString(36).slice(2, 14),
            trigger: trigger,
            source_path: window.location.pathname.slice(0, 1000),
            page_age_ms: Math.round(performance.now()),
            visibility: document.visibilityState,
            online: navigator.onLine
        };
        // Each optional diagnostic is best effort; a denied browser API must not
        // suppress the reload or the rest of its evidence.
        try {
            result.error_name = cleanText(error && error.name, 100);
            result.error_message = cleanText(typeof error === 'string' ? error : error && error.message, 1000);
            result.error_stack = cleanText(error && error.stack, 4000);
            result.error_file = cleanUrl(filename);
        } catch (e) {}
        try {
            var rawUrl = target && (target.src || target.href);
            if (!rawUrl && error) {
                var message = typeof error === 'string' ? error : error.message;
                var match = typeof message === 'string' && message.match(/(?:https?:\\/\\/[^\\s"'<>]+)?\\/_next\\/static\\/[^\\s"'<>)]*/);
                rawUrl = typeof error.request === 'string' ? error.request : match && match[0];
            }
            result.asset_url = cleanUrl(rawUrl);
            result.tag_name = target && target.tagName;
            result.link_rel = cleanText(target && target.rel, 100);
            result.link_as = cleanText(target && target.as, 100);
            if (rawUrl) {
                var entries = performance.getEntriesByName(new URL(rawUrl, window.location.href).href, 'resource');
                var timing = entries[entries.length - 1];
                // A failed request may have no timing entry. Do not mistake an
                // older successful load of this URL for its HTTP response.
                var age = timing && performance.now() - timing.responseEnd;
                if (timing && timing.responseEnd > 0 && age >= 0 && age <= 1000) {
                    result.resource_timing_age_ms = age;
                    result.resource_duration_ms = timing.duration;
                    result.resource_transfer_size = timing.transferSize;
                    // 0 can mean opaque/unsupported; it is not an HTTP failure code.
                    if (timing.responseStatus >= 100 && timing.responseStatus <= 599) {
                        result.resource_response_status = timing.responseStatus;
                    }
                }
            }
        } catch (e) {}
        try {
            result.effective_type = navigator.connection && navigator.connection.effectiveType;
            var previous = JSON.parse(sessionStorage.getItem(HISTORY_KEY) || 'null');
            if (previous && typeof previous.id === 'string' && previous.id.length <= 100 &&
                typeof previous.at === 'number' && now >= previous.at && now - previous.at < 600000) {
                result.previous_recovery_id = previous.id;
                result.since_previous_recovery_ms = now - previous.at;
            }
        } catch (e) {}
        return result;
    }
    window.__kalshiRecoveryDiagnostics = {
        capture: capture,
        remember: function (id) {
            try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify({ id: id, at: Date.now() })); } catch (e) {}
        }
    };
})();`;

export type RecoveryTrigger = 'resource_error' | 'window_error' | 'unhandledrejection' | 'import_error' | 'react_error';
export type RecoveryDiagnostics = Record<string, string | number | boolean>;

type CaptureHandle = {
    capture(trigger: RecoveryTrigger, error?: unknown): unknown;
    remember(id: string): void;
};

function getHandle(): CaptureHandle | undefined {
    return (window as unknown as { __kalshiRecoveryDiagnostics?: CaptureHandle }).__kalshiRecoveryDiagnostics;
}

/** Also sanitize on replay: storage is untrusted and older documents can hand it to a newer build. */
export function sanitizeRecoveryDiagnostics(raw: unknown): RecoveryDiagnostics {
    if (!raw || typeof raw !== 'object') return {};
    const source = raw as Record<string, unknown>;
    const result: RecoveryDiagnostics = {};
    const strings: Record<string, number> = {
        recovery_id: 100,
        previous_recovery_id: 100,
        trigger: 40,
        source_path: 1000,
        asset_url: 1000,
        tag_name: 20,
        link_rel: 100,
        link_as: 100,
        error_name: 100,
        error_message: 1000,
        error_stack: 4000,
        error_file: 1000,
        visibility: 20,
        effective_type: 20,
    };
    const cleanUrl = (value: string): string => {
        try {
            const url = new URL(value, window.location.href);
            return /^https?:$/.test(url.protocol) ? url.origin + url.pathname : '[url]';
        } catch {
            return '[url]';
        }
    };
    for (const [key, limit] of Object.entries(strings)) {
        const value = source[key];
        if (typeof value !== 'string') continue;
        const clean =
            key === 'asset_url' || key === 'error_file'
                ? cleanUrl(value)
                : key === 'source_path'
                  ? value.split(/[?#]/)[0]
                  : value.replace(/https?:\/\/[^\s"'<>]+|\/[^\s"'<>]*[?#][^\s"'<>]*/g, cleanUrl);
        result[key] = clean.slice(0, limit);
    }
    for (const key of [
        'page_age_ms',
        'resource_duration_ms',
        'resource_transfer_size',
        'resource_timing_age_ms',
        'since_previous_recovery_ms',
    ]) {
        const value = source[key];
        if (typeof value === 'number' && Number.isFinite(value) && value >= 0) result[key] = value;
    }
    const status = source.resource_response_status;
    if (typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599) {
        result.resource_response_status = status;
    }
    if (typeof source.online === 'boolean') result.online = source.online;
    return result;
}

export function captureRecoveryDiagnostics(trigger: RecoveryTrigger, error?: unknown): RecoveryDiagnostics {
    // Alternate layouts/tests may not install the inline helper. Keep a usable
    // correlation id there, and never let optional diagnostics break recovery.
    const fallback = { recovery_id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`, trigger };
    try {
        const captured = getHandle()?.capture(trigger, error);
        return sanitizeRecoveryDiagnostics(
            captured ?? {
                ...fallback,
                error_name: error instanceof Error ? error.name : undefined,
                error_message: error instanceof Error ? error.message : undefined,
                error_stack: error instanceof Error ? error.stack : undefined,
            },
        );
    } catch {
        return fallback;
    }
}

export function rememberRecovery(id: unknown): void {
    try {
        if (typeof id === 'string') getHandle()?.remember(id);
    } catch {
        // History is optional and must not interfere with the persisted record.
    }
}
